'use client';

/**
 * UploadPreview Component
 *
 * Displays a media preview with an overlay showing upload progress.
 *
 * Features:
 * 1. Real-time Progress Display: Shows circular progress indicator
 * 2. State Visualization:
 *    - Preview image with upload progress overlay
 *    - Processing state indication
 *    - Hover-visible cancel button
 * 3. Visual Feedback:
 *    - Dimmed preview during upload
 *    - Backdrop blur effect
 *    - Progress percentage display
 *
 * States:
 * - Uploading: Shows progress from 0-100%
 * - Processing: Shows when upload is complete but server is processing
 * - Error: Displays error state with option to cancel
 */

import { IMediaItem, IMediaPreview } from '@/lib/types/media/core';
import { Button, ProgressCircle } from '@heroui/react';
import { MediaPreview } from '../core/MediaPreview';

export interface UploadPreviewProps {
  preview: IMediaPreview;
  onCancel: () => void;
  aspectRatio?: 'square' | 'video' | 'auto';
  isUploading?: boolean;
  progress?: number;
}

export function UploadPreview({
  preview,
  onCancel,
  aspectRatio,
  isUploading = false,
  progress = 0
}: UploadPreviewProps) {
  const isProcessing = isUploading || preview.processingStatus === 'uploading' || preview.processingStatus === 'processing';

  return (
    <div className="relative group">
      {/* Preview Image - Dims during upload/processing */}
      <MediaPreview
        media={preview as unknown as IMediaItem} // Type cast needed as preview doesn't have all IMediaItem fields
        aspectRatio={aspectRatio}
        showControls={false}
        className={isProcessing ? 'opacity-75' : ''}
      />

      {/* Upload Progress Overlay - Shows during upload/processing */}
      {isProcessing && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/20 backdrop-blur-[1px]">
          <div className="flex flex-col items-center gap-2">
            <ProgressCircle
              aria-label="Upload progress"
              size="lg"
              value={progress}
              color="primary"
              showValueLabel={true}
              classNames={{
                svg: "w-20 h-20",
                indicator: "stroke-primary",
                track: "stroke-white/10",
                value: "text-sm font-semibold text-white"
              }}
            />
            <span className="text-sm text-white font-medium">
              {progress === 100 ? 'Processing...' : 'Uploading...'}
            </span>
          </div>
        </div>
      )}

      {/* Cancel Button - Visible on hover when not processing */}
      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button
          size="sm"
          variant="danger"
          onPress={onCancel}
          isDisabled={isProcessing}
          className="bg-background/80 backdrop-blur-sm"
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}
